import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { AssigneeLookup } from '@bliks-io/ui';
import type { ProcessDraftStep, StepTemplate } from '@bliks-io/contracts';
import { useInsertStepTemplate } from '@/api/mutations/useProcessDraft';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { usePermissions } from '@/hooks/usePermissions';
import { SaveStepTemplateDialog } from '../panels/SaveStepTemplateDialog';
import { InsertStepTemplateDialog } from '../panels/InsertStepTemplateDialog';

interface Params {
  processId: string;
  steps: ProcessDraftStep[];
  draftEvents: Array<{ _id: string; name?: string | null }>;
  assigneeLookup: AssigneeLookup;
}

/**
 * Step-template glue: the two menu entries' permission gates and the
 * save/insert dialogs.
 */
export function useStepTemplateActions({ processId, steps, draftEvents, assigneeLookup }: Params) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canCreate = can('StepTemplate', 'create');
  const canRead = can('StepTemplate', 'read');
  const insertStepTemplate = useInsertStepTemplate(processId);

  const [saveNodeIds, setSaveNodeIds] = useState<string[] | null>(null);
  const [insertAnchor, setInsertAnchor] = useState<{
    position: { x: number; y: number };
    laneKey?: string;
  } | null>(null);

  const openInsertDialog = useCallback(
    (position: { x: number; y: number }, laneKey?: string) =>
      setInsertAnchor({ position, laneKey }),
    []
  );
  const handleInsert = useCallback(
    (template: StepTemplate) => {
      if (!insertAnchor || insertStepTemplate.isPending) return;
      insertStepTemplate.mutate(
        { templateId: template._id, body: insertAnchor },
        {
          onSuccess: () => {
            setInsertAnchor(null);
            toast.success(t('stepTemplates.insertDialog.inserted', { name: template.name }));
          },
          onError: (error) => toastApiError(error, 'stepTemplates.error')
        }
      );
    },
    [insertAnchor, insertStepTemplate, t]
  );

  const dialogs: ReactNode = (
    <>
      {saveNodeIds && (
        <SaveStepTemplateDialog
          processId={processId}
          nodeIds={saveNodeIds}
          steps={steps}
          events={draftEvents}
          onClose={() => setSaveNodeIds(null)}
        />
      )}
      {insertAnchor && (
        <InsertStepTemplateDialog
          onInsert={handleInsert}
          onClose={() => setInsertAnchor(null)}
          isInserting={insertStepTemplate.isPending}
          assigneeLookup={assigneeLookup}
        />
      )}
    </>
  );

  return {
    contextMenuProps: {
      onSaveAsStepTemplate: canCreate ? setSaveNodeIds : undefined,
      onInsertStepTemplate: canRead ? openInsertDialog : undefined
    },
    dialogs
  };
}
